<script setup lang="ts">
/**
 * Star-born reward modal (M4C-29) — Figma `4062:26926`.
 *
 * The celebratory overlay shown when a member raises a new star: a lime-bordered
 * (`onboarding-border`) card on a blurred page, with a gold star, the
 * "Congratulations! / The star was born." heading, the just-born star highlighted
 * in the shared constellation renderer (M4C-26 `celebration` variant), supporting
 * copy, a "Find out more" link to the Star Glazing explainer (M4C-28) and a
 * Continue button. Chrome mirrors the onboarding tour shell (M4C-27) for
 * consistency: teleported overlay, body-scroll lock, Esc / backdrop / Continue to
 * dismiss.
 *
 * Reward feedback is ephemeral (M4C-25): the event carries only ids, so geometry
 * + progress are read back through the service (mock today, live M4C-30 later) —
 * no persisted reward state.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useGamificationService } from '@/composables/gamification';
import type { ConstellationDetail, RewardFeedbackEvent } from '@/types/gamification';

const props = defineProps<{
  /** The star-born event driving the modal. */
  event: RewardFeedbackEvent;
}>();

const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();
const localePath = useLocalePath();
const service = useGamificationService();

/** Generic upward 5-point star glyph (UI icon — not constellation geometry). */
const STAR_PATH =
  'M12 2.5l2.94 5.96 6.58.96-4.76 4.64 1.12 6.55L12 18.56l-5.88 3.09 1.12-6.55-4.76-4.64 6.58-.96z';

const overlayRef = ref<HTMLElement | null>(null);
/** Internal visibility — toggled to false on close so the leave transition runs. */
const show = ref(false);

const detail = ref<ConstellationDetail | null>(null);

/** "Find out more" routes to the Star Glazing / How it works page (M4C-28). */
const starGlazingPath = computed(() => localePath('/account/star-glazing'));

/** Stars still to raise to complete the constellation (drives the supporting copy). */
const remainingStars = computed(() => {
  if (!detail.value) {
    return null;
  }
  return detail.value.stars.filter((s) => s.state !== 'earned').length;
});

/**
 * Supporting line: "Keep up the good work to open N stars and collect <name>."
 * Falls back to the event's own message, then a generic line, when there's no
 * constellation context or it's already complete.
 */
const supportingCopy = computed(() => {
  if (detail.value && remainingStars.value && remainingStars.value > 0) {
    return t('account.reward.modal.keep_up', {
      count: remainingStars.value,
      name: detail.value.name,
    });
  }
  if (detail.value && remainingStars.value === 0) {
    return t('account.reward.modal.collected', { name: detail.value.name });
  }
  return props.event.message ?? t('account.reward.modal.keep_up_generic');
});

async function loadDetail(constellationId?: string): Promise<void> {
  if (!constellationId) {
    detail.value = null;
    return;
  }
  try {
    detail.value = await service.getConstellationDetail(constellationId);
  } catch {
    // The modal degrades gracefully to the gold-star hero without geometry.
    detail.value = null;
  }
}

function requestClose(): void {
  show.value = false;
}

onMounted(() => {
  show.value = true;
  if (import.meta.client) {
    document.body.style.overflow = 'hidden';
    void overlayRef.value?.focus();
  }
  void loadDetail(props.event.constellation_id);
});

watch(
  () => props.event.constellation_id,
  (id) => loadDetail(id),
);

function unlockScroll(): void {
  if (import.meta.client) {
    document.body.style.overflow = '';
  }
}

/** After the leave transition: restore scroll and tell the host to drop the event. */
function onAfterLeave(): void {
  unlockScroll();
  emit('close');
}

onBeforeUnmount(unlockScroll);
</script>

<template>
  <Teleport to="body">
    <Transition
      appear
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-200 ease-in"
      leave-to-class="opacity-0"
      @after-leave="onAfterLeave"
    >
      <div
        v-if="show"
        ref="overlayRef"
        tabindex="-1"
        class="fixed inset-0 z-[1100] flex justify-center overflow-y-auto bg-white focus:outline-none md:items-center md:bg-white/30 md:p-4 md:backdrop-blur-[7.5px]"
        role="dialog"
        aria-modal="true"
        :aria-label="t('account.reward.modal.aria_label')"
        @keydown.esc="requestClose"
        @click.self="requestClose"
      >
        <!-- Outer frame: 4px white padding + soft shadow around the lime card -->
        <div
          class="flex w-full flex-col self-stretch rounded-[6px] bg-white p-1 shadow-[0_0_7.5px_rgba(77,112,118,0.2)] md:max-w-[588px] md:self-center"
        >
          <!-- Inner card: lime border, centered hero / constellation / copy / CTA -->
          <div
            class="flex flex-1 flex-col items-center rounded-[4px] border border-onboarding-border bg-white p-6 font-app text-fontColor md:p-8"
          >
            <!-- Close (top-right) -->
            <div class="flex w-full justify-end">
              <button
                type="button"
                class="-m-2 inline-flex h-9 w-9 items-center justify-center rounded text-fontColor-w60 hover:text-fontColor focus:outline-none focus-visible:ring-2 focus-visible:ring-onboarding-border"
                :aria-label="t('account.reward.modal.close')"
                @click="requestClose"
              >
                <svg class="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M4 4l8 8M12 4l-8 8"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
            </div>

            <!-- Hero: gold star + two-line heading -->
            <div class="flex flex-col items-center gap-6 text-center">
              <svg viewBox="0 0 24 24" class="h-14 w-14 fill-star" aria-hidden="true">
                <path :d="STAR_PATH" />
              </svg>
              <div>
                <p class="m-0 font-app text-lead text-fontColor">
                  {{ t('account.reward.modal.title') }}
                </p>
                <p class="m-0 font-app text-h2 text-fontColor">
                  {{ t('account.reward.modal.subtitle') }}
                </p>
              </div>
            </div>

            <!-- Constellation with the just-born star highlighted -->
            <div class="mt-6 w-[180px]">
              <GamiConstellation
                v-if="detail"
                :stars="detail.constellation.stars_data"
                :segments="detail.constellation.segments_data"
                :progress="detail.stars"
                :highlight-star-id="event.star_id"
                :show-labels="false"
                variant="celebration"
                :name="detail.name"
              />
              <div v-else class="aspect-square w-full animate-pulse rounded bg-fontColor-w95" />
            </div>

            <!-- Supporting copy + special-quests link -->
            <p class="m-0 mt-6 max-w-[420px] text-center font-app text-body text-fontColor">
              {{ supportingCopy }}
            </p>
            <p class="m-0 mt-6 max-w-[420px] text-center font-app text-microcopy text-fontColor">
              {{ t('account.reward.modal.quests') }}
              <NuxtLink
                :to="starGlazingPath"
                class="font-app text-microcopy-strong text-link hover:underline"
                @click="requestClose"
              >
                {{ t('account.reward.modal.find_out_more') }}</NuxtLink
              >.
            </p>

            <!-- CTA -->
            <GamiButton class="mt-8" @click="requestClose">
              {{ t('account.reward.modal.continue') }}
            </GamiButton>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
